import { Check, Copy, Printer, RotateCcw, Share2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";

type CalculatorActionsProps = {
  values: Record<string, string | number>;
  resultText: string;
  onReset: () => void;
  queryPrefix?: string;
};

export function CalculatorActions({ values, resultText, onReset, queryPrefix = "calc" }: CalculatorActionsProps) {
  const [copied, setCopied] = useState(false);

  async function copyText(text: string, success: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(success);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed. Please select and copy the result manually.");
    }
  }

  function shareUrl() {
    const url = new URL(window.location.href);
    for (const key of Array.from(url.searchParams.keys())) {
      if (key.startsWith(`${queryPrefix}_`)) url.searchParams.delete(key);
    }
    for (const [key, value] of Object.entries(values)) {
      url.searchParams.set(`${queryPrefix}_${key}`, String(value));
    }
    window.history.replaceState(null, "", url);
    if (navigator.share) {
      void navigator.share({ title: document.title, text: resultText, url: url.toString() }).catch(() => undefined);
      return;
    }
    void copyText(url.toString(), "Shareable calculation link copied");
  }

  return (
    <div className="mt-4 flex flex-wrap gap-2" aria-label="Calculation actions">
      <Toaster position="bottom-center" />
      <Button type="button" variant="outline" size="sm" onClick={() => void copyText(resultText, "Results copied") }>
        {copied ? <Check /> : <Copy />} <span>{copied ? "Copied" : "Copy results"}</span>
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => window.print()}>
        <Printer /> <span>Print / Save PDF</span>
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={shareUrl}>
        <Share2 /> <span>Share link</span>
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onReset}>
        <RotateCcw /> <span>Reset</span>
      </Button>
    </div>
  );
}

export function readCalculatorQuery(prefix: string, allowedKeys: string[]): Record<string, string> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const allowed = new Set(allowedKeys);
  const values: Record<string, string> = {};
  for (const key of allowed) {
    const value = params.get(`${prefix}_${key}`);
    if (value !== null && value.length <= 120 && allowed.has(key)) values[key] = value;
  }
  return values;
}